/**
 * VIDEO FAN CAROUSEL
 * ----------------------------------------------------------------------------------------------------------------
 * Vanilla-JS port of a fanned-card carousel (originally a React + GSAP component): a row of vertical cards spread
 * out like a hand of cards, the center one full-size, each neighbor progressively smaller/rotated/receded. Hovering
 * a card raises it and pushes its neighbors aside. It's a real slider: swiping (or the prev/next arrows) rotates
 * which card sits in the center slot, one step at a time, wrapping around — this works the same whether there are
 * 3 cards or 30; with more than 7, only 7 are ever visible at once and the rest page in/out from the sides.
 *
 * No GSAP/React here (the theme doesn't ship either) — positions are plain CSS transforms driven by JS, animated by
 * a CSS transition with an overshoot easing curve that reads as "elastic" without needing a real physics library.
 */
(() => {
  if (window.customElements.get('video-fan-carousel')) return;

  const MAX_VISIBLE = 7;
  const SWIPE_THRESHOLD_PX = 40;

  // Fixed fan geometry for 7+ cards, in rem, one entry per slot (0 = leftmost … 6 = rightmost).
  const FAN_POSITIONS = [
    { rot: -21, scale: 0.7756, x: -30, y: 7.3, zIndex: 1 },
    { rot: -14, scale: 0.8498, x: -22, y: 4.0, zIndex: 2 },
    { rot: -7, scale: 0.9346, x: -11, y: 1.3, zIndex: 3 },
    { rot: 0, scale: 1.0, x: 0, y: 0.0, zIndex: 5 },
    { rot: 7, scale: 0.9346, x: 11, y: 1.3, zIndex: 3 },
    { rot: 14, scale: 0.8498, x: 22, y: 4.0, zIndex: 2 },
    { rot: 21, scale: 0.7756, x: 30, y: 7.3, zIndex: 1 },
  ];

  // Width (px) at which the fan geometry above reads as designed (1:1 scale). Below it, offsets shrink
  // proportionally so cards don't fan out past the viewport edge on small screens.
  const DESIGN_WIDTH = 1280;
  const MIN_MULTIPLIER = 0.28;

  function getMultiplier(containerWidth) {
    return Math.max(MIN_MULTIPLIER, Math.min(1, containerWidth / DESIGN_WIDTH));
  }

  function getSlotConfig(slotCount, slot) {
    if (slotCount >= MAX_VISIBLE) return FAN_POSITIONS[slot];

    const center = slotCount >> 1;
    const distance = slotCount > 1 ? (slot - center) / center : 0;
    const absDistance = Math.abs(distance);

    return {
      rot: distance * 21,
      scale: 1.0 - 0.2244 * absDistance * absDistance,
      x: distance * 30,
      y: absDistance * absDistance * 7.3,
      // Capped at 5, not 10 — the site header is also z-index: 10 (assets/theme.css .header), and an exact
      // tie resolves by DOM order, which put this carousel (later in the page) on top of the header.
      zIndex: 5 - Math.abs(slot - center),
    };
  }

  class VideoFanCarousel extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll(':scope .fan-card'));
      this.total = this.cards.length;
      if (!this.total) return;

      // Only cards beyond the 7-slot fan ever page in/out from off-screen; with 7 or fewer, every card
      // stays visible and cycling just rotates which slot (position in the fan) each one occupies.
      this.slotCount = Math.min(this.total, MAX_VISIBLE);
      this.centerIndex = this.slotCount >> 1;
      this.previouslyVisible = new Set();
      this.isAnimating = false;
      this.hoveredSlot = null;
      this.leaveTimer = null;

      this.prevButton = this.querySelector('[data-direction="prev"]');
      this.nextButton = this.querySelector('[data-direction="next"]');
      this.dots = Array.from(this.querySelectorAll('.video-fan-carousel__dot'));

      this.prevButton?.addEventListener('click', () => this.cycle('left'));
      this.nextButton?.addEventListener('click', () => this.cycle('right'));

      this.cards.forEach((card, index) => {
        card.addEventListener('mouseenter', () => this.onCardEnter(index));
      });
      this.addEventListener('mouseleave', () => this.onContainerLeave());

      this._onPointerDown = this.onPointerDown.bind(this);
      this._onPointerMove = this.onPointerMove.bind(this);
      this._onPointerUp = this.onPointerUp.bind(this);
      this.addEventListener('pointerdown', this._onPointerDown);

      this._onResize = () => this.render({ isResize: true });
      window.addEventListener('resize', this._onResize);

      this.render({ isFirstMount: true });

      // Videos only load/play while the carousel is near the viewport
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          this._inView = entries[entries.length - 1].isIntersecting;
          this.syncVideos();
        }, { rootMargin: '200px 0px' });
        this._io.observe(this);
      } else {
        this._inView = true;
        this.syncVideos();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);
      if (this.leaveTimer) clearTimeout(this.leaveTimer);
      if (this._io) this._io.disconnect();
    }

    // Maps card index -> slot (position in the fan, 0..slotCount-1). Rotates with centerIndex — this is
    // what makes prev/next (and swipe) actually move a *different* card into the center every time, instead
    // of just re-drawing the same static fan.
    getVisibleMap(center) {
      const half = this.slotCount >> 1;
      const map = new Map();

      for (let slot = 0; slot < this.slotCount; slot++) {
        const index = (((center + slot - half) % this.total) + this.total) % this.total;
        map.set(index, slot);
      }

      return map;
    }

    cycle(direction) {
      if (this.isAnimating || this.total <= 1) return;

      this.isAnimating = true;
      this.direction = direction;
      this.centerIndex = direction === 'right' ? (this.centerIndex + 1) % this.total : (this.centerIndex - 1 + this.total) % this.total;

      this.render();

      window.setTimeout(() => {
        this.isAnimating = false;
      }, 650);
    }

    onCardEnter(index) {
      if (this.isAnimating) return;
      if (this.leaveTimer) {
        clearTimeout(this.leaveTimer);
        this.leaveTimer = null;
      }

      const slot = this.getVisibleMap(this.centerIndex).get(index);
      if (slot === undefined || this.hoveredSlot === slot) return;

      this.hoveredSlot = slot;
      this.applyLayout({ isFirstMount: false, hoveredSlot: slot });
    }

    onContainerLeave() {
      if (this.isAnimating) return;
      if (this.leaveTimer) clearTimeout(this.leaveTimer);

      this.leaveTimer = window.setTimeout(() => {
        this.hoveredSlot = null;
        this.applyLayout({ isFirstMount: false, hoveredSlot: null });
      }, 50);
    }

    // Swipe-to-cycle: a plain threshold gesture (drag past ~40px horizontally, further than vertically, then
    // release) rather than a live 1:1 drag-follow — each swipe advances exactly one card, same as one arrow
    // click, and repeating the swipe keeps advancing. Pointer Events cover touch, mouse-drag and pen in one.
    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.swipeStartX = event.clientX;
      this.swipeStartY = event.clientY;
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
    }

    onPointerMove(_event) {
      // No live drag-follow — positions only update on release, in onPointerUp.
    }

    onPointerUp(event) {
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
      window.removeEventListener('pointercancel', this._onPointerUp);

      if (this.swipeStartX === undefined) return;

      const dx = event.clientX - this.swipeStartX;
      const dy = event.clientY - this.swipeStartY;
      this.swipeStartX = undefined;
      this.swipeStartY = undefined;

      if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) return;

      // Swipe right (finger moves right, dx > 0) -> next. Swipe left -> previous.
      this.cycle(dx > 0 ? 'right' : 'left');
    }

    render(options = {}) {
      const visibleMap = this.getVisibleMap(this.centerIndex);
      this.applyLayout({ ...options, visibleMap });
      this.updateDots();
      this.syncVideos();
    }

    // Only the center (active) card downloads and plays its video; the others stay unloaded/paused until a swipe or
    // arrow brings them to the center (each creator video is 10-20 MB, so loading all 7+ at once froze phones).
    syncVideos() {
      const inView = this._inView === true;
      this.cards.forEach((card, index) => {
        const video = card.querySelector('video');
        if (!video) return;
        if (index === this.centerIndex && inView) {
          if (!video._fanOn) {
            video._fanOn = true;
            video.querySelectorAll('source[data-lazy-src]').forEach((source) => {
              source.src = source.getAttribute('data-lazy-src');
              source.removeAttribute('data-lazy-src');
            });
            video.preload = 'metadata';
            video.load();
          }
          if (video.hasAttribute('data-lazy-autoplay')) {
            video.muted = true;
            const played = video.play();
            if (played && played.catch) played.catch(() => {});
          }
        } else if (video._fanOn && !video.paused) {
          video.pause();
        }
      });
    }

    applyLayout({ isFirstMount = false, isResize = false, hoveredSlot = this.hoveredSlot, visibleMap = this.getVisibleMap(this.centerIndex) } = {}) {
      const containerWidth = this.getBoundingClientRect().width || DESIGN_WIDTH;
      const multiplier = getMultiplier(containerWidth);
      const previouslyVisible = this.previouslyVisible;
      const direction = this.direction;

      const visibleSlots = Array.from(visibleMap.values());
      const centerSlot = visibleSlots.length ? visibleSlots[visibleSlots.length >> 1] : 0;

      this.cards.forEach((card, index) => {
        const slot = visibleMap.get(index);
        const wasVisible = previouslyVisible.has(index);

        if (slot === undefined) {
          if (wasVisible) {
            const exitX = direction === 'right' ? -40 : 40;
            card.style.transform = `translate3d(${exitX * multiplier}rem, 0, 0) rotate(${direction === 'right' ? -30 : 30}deg) scale(.5)`;
            card.style.opacity = '0';
            card.style.zIndex = '0';
          } else if (isFirstMount) {
            card.style.transition = 'none';
            card.style.transform = 'scale(.3)';
            card.style.opacity = '0';
            card.style.zIndex = '0';
          }
          card.style.pointerEvents = 'none';
          return;
        }

        const base = getSlotConfig(this.slotCount, slot);
        let x = base.x * multiplier;
        let y = base.y * multiplier;
        let rot = base.rot;
        let scale = base.scale;

        if (hoveredSlot !== null) {
          if (slot === hoveredSlot) {
            y -= 2.5 * multiplier;
            scale *= 1.08;
          } else {
            const distance = Math.abs(slot - hoveredSlot);
            const normalized = centerSlot > 0 ? (slot - centerSlot) / centerSlot : 0;
            const pushStrength = 8 * (1 - Math.abs(normalized)) * (1 + 0.2 * Math.max(0, 3 - distance)) * multiplier;

            if (slot < hoveredSlot) {
              x -= pushStrength;
              rot -= 3 / (distance + 1);
            } else {
              x += pushStrength;
              rot += 3 / (distance + 1);
            }
          }
        }

        if (isFirstMount) {
          card.style.transition = 'none';
          card.style.transform = `translate3d(0, ${12 * multiplier}rem, 0) rotate(0deg) scale(.5)`;
          card.style.opacity = '0';
          // Force the browser to apply the hidden state above before animating to the final one below.
          card.getBoundingClientRect();
          card.style.transition = '';
          card.style.transitionDelay = `${0.2 + slot * 0.06}s`;
        } else if (!wasVisible && !isResize) {
          const enterX = direction === 'right' ? 40 : -40;
          card.style.transition = 'none';
          card.style.transform = `translate3d(${enterX * multiplier}rem, ${y}rem, 0) rotate(${direction === 'right' ? 30 : -30}deg) scale(.5)`;
          card.style.opacity = '0';
          card.getBoundingClientRect();
          card.style.transition = '';
          card.style.transitionDelay = '0s';
        } else if (!isFirstMount) {
          card.style.transitionDelay = isResize ? '0s' : `${Math.abs(slot - (hoveredSlot ?? centerSlot)) * 0.02}s`;
        }

        card.style.transform = `translate3d(${x}rem, ${y}rem, 0) rotate(${rot}deg) scale(${scale})`;
        card.style.opacity = '1';
        card.style.zIndex = String(base.zIndex);
        card.style.pointerEvents = 'auto';
      });

      this.previouslyVisible = new Set(visibleMap.keys());
    }

    updateDots() {
      if (!this.dots.length) return;

      this.dots.forEach((dot, index) => {
        dot.classList.toggle('video-fan-carousel__dot--active', index === this.centerIndex);
      });
    }
  }

  window.customElements.define('video-fan-carousel', VideoFanCarousel);
})();
